import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { cleanPhoneNumber, isPhoneEmptyOrValid } from '../../../../../utils/phoneUtils';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';
import { createContact, updateContact } from '../../../../../services/contactServices';
import { formatABN } from '../../../../../utils/formatABN';
import { autocompleteContact } from '../../../../../services/formBuilderService';
import CustomCreatableDropdown from 'custom-components/shared/CustomCreatableDropdown';
import useDropdown from 'hooks/useDropdown';
import useSuburbRecommendation from 'hooks/useSuburbRecommendation';
import { DropdownEnum } from 'enum/DropdownEnum';

export default function ContactModal({ isOpen, onClose, contactData, onSuccess }) {
  const isEditing = !!contactData;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fullName, setFullName] = useState('');
  const [fullNameError, setFullNameError] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState(false);

  const [streetAddress, setStreetAddress] = useState('');
  const [suburb, setSuburb] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [state, setState] = useState('');
  const [abn, setAbn] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [suggestions, setSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [searchingField, setSearchingField] = useState({});
  const searchTimeoutRef = useRef({});

  const handleSearchContact = (fieldName, value, searchType) => {
    if (searchTimeoutRef.current[fieldName]) {
      clearTimeout(searchTimeoutRef.current[fieldName]);
    }

    const trimmed = (value || '').trim();
    if (trimmed.length < 4) {
      setSuggestions(prev => ({ ...prev, [fieldName]: [] }));
      setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
      setSearchingField(prev => ({ ...prev, [fieldName]: false }));
      return;
    }

    setSearchingField(prev => ({ ...prev, [fieldName]: true }));
    setShowSuggestions(prev => ({ ...prev, [fieldName]: true }));

    searchTimeoutRef.current[fieldName] = setTimeout(async () => {
      try {
        const response = await autocompleteContact(trimmed, searchType);
        if (response.success && response.data && response.data.length > 0) {
          setSuggestions(prev => ({ ...prev, [fieldName]: response.data }));
          setShowSuggestions(prev => ({ ...prev, [fieldName]: true }));
        } else {
          setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
        }
      } catch (err) {
        console.error("Autocomplete error:", err);
        setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
      } finally {
        setSearchingField(prev => ({ ...prev, [fieldName]: false }));
      }
    }, 400);
  };

  const handleSelectContactSuggestion = (fieldName, contact) => {
    if (contact.fullName) setFullName(contact.fullName);
    if (contact.email) {
      setEmail(contact.email);
      setEmailError('');
    }
    if (contact.phoneNumber) {
      setPhoneNumber(
        (contact.phoneNumber && !String(contact.phoneNumber).startsWith('+') && !String(contact.phoneNumber).startsWith('0'))
          ? `+61${contact.phoneNumber}`
          : contact.phoneNumber
      );
      setPhoneError('');
    }
    if (contact.streetAddress) setStreetAddress(contact.streetAddress);
    if (contact.suburb) setSuburb(contact.suburb);
    if (contact.postalCode || contact.postcode) setPostalCode(contact.postalCode || contact.postcode);
    if (contact.state) setState(contact.state);
    if (contact.companyName || contact.company_name) setCompanyName(contact.companyName || contact.company_name);
    if (contact.abnNumber || contact.abn || contact.Abn) setAbn(contact.abnNumber || contact.abn || contact.Abn);

    setShowSuggestions({ fullName: false, email: false, phoneNumber: false });
  };

  const renderSuggestionsDropdown = (fieldName) => {
    const isSearching = searchingField[fieldName];
    const hasSuggestions = suggestions[fieldName] && suggestions[fieldName].length > 0;
    const isOpen = showSuggestions[fieldName];

    if (!isSearching && !hasSuggestions) return null;

    return (
      <div
        className={`absolute z-[80] top-full left-0 right-0 mt-1 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg shadow-xl max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-dark-600 transition-all duration-200 ease-in-out origin-top transform ${isOpen ? "opacity-100 translate-y-0 scale-100 pointer-events-auto" : "opacity-0 -translate-y-2 scale-95 pointer-events-none"}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {isSearching ? (
          <div className="flex items-center justify-center gap-2.5 px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
            <svg className="animate-spin h-4 w-4 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <span>Searching contacts...</span>
          </div>
        ) : (
          suggestions[fieldName].map((contact) => (
            <div
              key={contact.contactId || contact.id}
              className="p-3 hover:bg-primary-50 dark:hover:bg-primary-900/30 cursor-pointer text-left transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectContactSuggestion(fieldName, contact);
              }}
            >
              <div className="font-semibold text-sm text-gray-800 dark:text-white truncate">{contact.fullName}</div>
              <div className="flex flex-col gap-0.5 mt-1 text-xs text-gray-500 dark:text-gray-400">
                {contact.phoneNumber && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">📱</span>
                    <span>{contact.phoneNumber}</span>
                  </div>
                )}
                {contact.email && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">📧</span>
                    <span className="truncate">{contact.email}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };


  const suburbDropdown = useDropdown(DropdownEnum.Suburb, isOpen);
  const stateDropdown = useDropdown(DropdownEnum.State, isOpen);
  const companyDropdown = useDropdown(DropdownEnum.Company, isOpen);

  // Autofill State + PostalCode when the selected/typed Suburb matches a known
  // recommendation. Never fires for State-only entry — only Suburb resolves State/PostalCode.
  const lookupSuburbRecommendation = useSuburbRecommendation((rec) => {
    setState(rec.state || "");
    setPostalCode(rec.postalCode || "");
  });

  useEffect(() => {
    let isStale = false;
    if (isOpen) {
      if (isEditing && contactData) {
        const initialFullName =
          contactData.fullName ||
          contactData.FullName ||
          contactData.name ||
          contactData.Name ||
          "";
        const initialEmail = contactData.email || contactData.Email || "";
        const initialPhoneRaw =
          contactData.phoneNumber ||
          contactData.PhoneNumber ||
          contactData.phone ||
          contactData.Phone ||
          "";

        setFullName(initialFullName);
        setEmail(initialEmail);

        let phone = initialPhoneRaw;
        if (phone) {
          if (!phone.startsWith("+")) {
            if (phone.startsWith("0")) {
              phone = "+61" + phone.substring(1);
            } else {
              phone = "+61" + phone;
            }
          }
        } else {
          phone = "+61";
        }
        setPhoneNumber(phone);

        const initialStreetAddress =
          contactData.streetAddress ||
          contactData.StreetAddress ||
          contactData.address ||
          contactData.Address ||
          contactData.ContactStreetAddress ||
          "";
        const initialSuburb =
          contactData.suburb ||
          contactData.Suburb ||
          contactData.city ||
          contactData.City ||
          contactData.ContactSuburb ||
          contactData.suburbName ||
          "";
        const initialPostalCode =
          contactData.postalCode ||
          contactData.PostalCode ||
          contactData.postCode ||
          contactData.postcode ||
          contactData.ContactPostalCode ||
          "";
        const initialState =
          contactData.state ||
          contactData.State ||
          contactData.ContactState ||
          contactData.stateName ||
          "";
        const initialCompanyName =
          contactData.companyName ||
          contactData.CompanyName ||
          contactData.company_name ||
          contactData.ContactCompanyName ||
          "";

        setStreetAddress(initialStreetAddress);
        setSuburb(initialSuburb);
        setPostalCode(initialPostalCode);
        setState(initialState);
        setAbn(
          formatABN(
            contactData.abnNumber ||
              contactData.ABNNumber ||
              contactData.abn ||
              contactData.Abn ||
              contactData.ABN ||
              contactData.abn_number ||
              contactData.ContactABNNumber ||
              ""
          )
        );
        setCompanyName(initialCompanyName);

        // Fetch the full contact record from the backend ONLY if contactData is actually missing
        // address/company details - contactData already carries these when editing from the Lead's
        // Contacts tab, so this backfill is for leaner callers that pass just name/phone/email. Also
        // guard against this (and the modal re-opening before it resolves) applying a stale/mismatched
        // response on top of fields that are already correctly populated.
        const hasMissingDetails =
          !initialStreetAddress ||
          !initialSuburb ||
          !initialPostalCode ||
          !initialState ||
          !initialCompanyName;
        const searchTerm = initialEmail || initialFullName || initialPhoneRaw;
        const searchType = initialEmail ? "email" : initialFullName ? "name" : "phone";

        if (hasMissingDetails && searchTerm) {
          autocompleteContact(searchTerm, searchType)
            .then((res) => {
              if (isStale) return;
              if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
                const targetId = String(
                  contactData.id || contactData.contactId || contactData.ContactId || ""
                );
                const fullContact =
                  (targetId
                    ? res.data.find(
                        (c) =>
                          String(c.contactId || c.id || c.ContactId || "") === targetId
                      )
                    : null) || res.data[0];

                if (fullContact) {
                  if (fullContact.fullName || fullContact.name || fullContact.FullName) {
                    setFullName(
                      fullContact.fullName || fullContact.FullName || fullContact.name
                    );
                  }
                  if (fullContact.email || fullContact.Email) {
                    setEmail(fullContact.email || fullContact.Email);
                  }
                  if (fullContact.phoneNumber || fullContact.phone || fullContact.PhoneNumber) {
                    let p =
                      fullContact.phoneNumber ||
                      fullContact.PhoneNumber ||
                      fullContact.phone;
                    if (p && !p.startsWith("+")) {
                      p = p.startsWith("0") ? "+61" + p.substring(1) : "+61" + p;
                    }
                    if (p) setPhoneNumber(p);
                  }
                  if (fullContact.streetAddress || fullContact.StreetAddress || fullContact.address) {
                    setStreetAddress(
                      fullContact.streetAddress ||
                        fullContact.StreetAddress ||
                        fullContact.address
                    );
                  }
                  if (fullContact.suburb || fullContact.Suburb || fullContact.city) {
                    setSuburb(
                      fullContact.suburb || fullContact.Suburb || fullContact.city
                    );
                  }
                  if (fullContact.postalCode || fullContact.postcode || fullContact.PostalCode) {
                    setPostalCode(
                      fullContact.postalCode ||
                        fullContact.postcode ||
                        fullContact.PostalCode
                    );
                  }
                  if (fullContact.state || fullContact.State) {
                    setState(fullContact.state || fullContact.State);
                  }
                  if (
                    fullContact.companyName ||
                    fullContact.CompanyName ||
                    fullContact.company_name
                  ) {
                    setCompanyName(
                      fullContact.companyName ||
                        fullContact.CompanyName ||
                        fullContact.company_name
                    );
                  }
                  if (
                    fullContact.abnNumber ||
                    fullContact.abn ||
                    fullContact.Abn ||
                    fullContact.ABNNumber
                  ) {
                    setAbn(
                      formatABN(
                        fullContact.abnNumber ||
                          fullContact.abn ||
                          fullContact.Abn ||
                          fullContact.ABNNumber
                      )
                    );
                  }
                }
              }
            })
            .catch((err) => {
              if (!isStale) console.error("Failed to fetch full contact record in ContactModal:", err);
            });
        }
      } else {
        setFullName("");
        setEmail("");
        setPhoneNumber("+61");
        setStreetAddress("");
        setSuburb("");
        setPostalCode("");
        setState("");
        setAbn("");
        setCompanyName("");
      }
      setFullNameError(false);
      setPhoneError(false);
      setEmailError(false);
      setIsSubmitting(false);

      return () => {
        isStale = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditing, contactData?.id || contactData?.contactId || contactData?.ContactId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName || !fullName.trim()) {
      setFullNameError(true);
      toast.error("Full Name is required");
      return;
    }
    setFullNameError(false);

    const cleanPhone = cleanPhoneNumber(phoneNumber);
    const cleanEmail = (email && email.trim() !== '') ? email.trim() : null;

    if (!cleanPhone && !cleanEmail) {
      setPhoneError(true);
      setEmailError(true);
      toast.error("At least email or phone number is required");
      return;
    }

    if (cleanPhone && !isValidPhoneNumber(cleanPhone)) {
      setPhoneError(true);
      toast.error("Please enter a valid phone number");
      return;
    }
    setPhoneError(false);

    if (cleanEmail) {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(cleanEmail)) {
        setEmailError(true);
        toast.error("Valid email address is required");
        return;
      }
    }
    setEmailError(false);

    setIsSubmitting(true);
    try {
      let res;
      const cleanAbn = abn ? abn.replace(/\s+/g, '') : null;
      const payload = {
        fullName,
        email: cleanEmail,
        phoneNumber: cleanPhone,
        leadId: null,
        city: null,
        postalCode: postalCode || null,
        state: state || null,
        suburb: suburb || null,
        streetAddress: streetAddress || null,
        companyName: companyName || null,
        abnNumber: cleanAbn ? Number(cleanAbn) : null
      };

      let targetContactId = null;
      if (isEditing) {
        targetContactId =
          contactData?.contactId ||
          contactData?.ContactId ||
          contactData?.id ||
          contactData?.Id ||
          contactData?.clientId ||
          contactData?.ClientId ||
          contactData?.userId ||
          contactData?.UserId;

        res = await updateContact({
          contactId: targetContactId,
          ContactId: targetContactId,
          id: targetContactId,
          Id: targetContactId,
          ...payload
        });
        if(res?.success){
          toast.success("Contact updated successfully");
        }
      } else {
        res = await createContact(payload);
        if(res?.success){
          toast.success("Contact created successfully");
        }
      }

      if (!res.success) {
        throw new Error(res.error);
      }

      if (onSuccess) onSuccess({ ...payload, id: targetContactId || res?.data?.contactId || res?.data?.id, contactId: targetContactId || res?.data?.contactId, ContactId: targetContactId || res?.data?.ContactId });
      onClose();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} contact:`, error);
      toast.error(error.message || `Failed to ${isEditing ? 'update' : 'create'} contact`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[350]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform flex flex-col max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-dark-800 p-6 text-left align-middle shadow-xl transition-all border border-gray-100 dark:border-dark-700">
                <div className="flex justify-between items-center mb-5 shrink-0">
                  <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-gray-900 dark:text-white">
                    {isEditing ? 'Edit Contact' : 'Add Contact'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
                  <div className="flex-1 overflow-y-auto space-y-4 pb-2 pr-2 -mr-2">
                    <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={fullName}
                          onChange={(e) => {
                            setFullName(e.target.value);
                            if (e.target.value.trim()) setFullNameError(false);
                            handleSearchContact('fullName', e.target.value, 'name');
                          }}
                          onBlur={() => setShowSuggestions(prev => ({ ...prev, fullName: false }))}
                          className={`w-full rounded-lg border bg-white dark:bg-dark-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none transition-colors ${
                            fullNameError
                              ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                              : 'border-gray-300 dark:border-dark-600 focus:border-primary-500 focus:ring-1 focus:ring-primary-500'
                          }`}
                          placeholder="John Doe"
                        />
                        {renderSuggestionsDropdown('fullName')}
                      </div>
                      {fullNameError && (
                        <p className="mt-1 text-xs text-red-500 animate-fadeIn">
                          Full Name is required
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone Number 
                      </label>
                      <div className="relative">
                        <div className={`relative rounded-lg border transition-all duration-200 bg-white dark:bg-dark-900 flex items-center min-h-[38px] ${
                          phoneError 
                            ? 'border-red-500 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500' 
                            : 'border-gray-300 dark:border-dark-600 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'
                        }`}>
                          <PhoneInput
                            international
                            defaultCountry="AU"
                            limitMaxLength={true}
                            countrySelectProps={{ tabIndex: -1 }}
                            countryCallingCodeEditable={false}
                            value={phoneNumber}
                            onChange={(value) => {
                              setPhoneNumber(value || '');
                              handleSearchContact('phoneNumber', value || '', 'phone');
                              if (!isPhoneEmptyOrValid(value)) {
                                setPhoneError(true);
                              } else {
                                setPhoneError(false);
                                if (emailError) setEmailError(false);
                              }
                            }}
                            onBlur={() => setShowSuggestions(prev => ({ ...prev, phoneNumber: false }))}
                            className="px-3 py-2 w-full react-phone-input"
                            numberInputProps={{
                              className: "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none",
                              required: false,
                              onFocus: (e) => {
                                const target = e.target;
                                setTimeout(() => {
                                  if (target) {
                                    const valLen = target.value.length;
                                    target.setSelectionRange(valLen, valLen);
                                  }
                                }, 10);
                              },
                              onKeyUp: (e) => {
                                if (e.key === 'Tab') {
                                  const valLen = e.target.value.length;
                                  e.target.setSelectionRange(valLen, valLen);
                                }
                              }
                            }}
                          />
                        </div>
                        {renderSuggestionsDropdown('phoneNumber')}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email Address 
                      </label>
                      <div className={`relative rounded-lg border transition-all duration-200 bg-white dark:bg-dark-900 ${
                        emailError 
                          ? 'border-red-500 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500' 
                          : 'border-gray-300 dark:border-dark-600 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'
                      }`}>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEmail(val);
                            handleSearchContact('email', val, 'email');
                            if (val) {
                              const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                              if (!emailRegex.test(val)) {
                                setEmailError(true);
                              } else {
                                setEmailError(false);
                                if (phoneError) setPhoneError(false);
                              }
                            } else {
                              setEmailError(false);
                            }
                          }}
                          onBlur={() => setShowSuggestions(prev => ({ ...prev, email: false }))}
                          className="w-full bg-transparent border-none focus:ring-0 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
                          placeholder="john@example.com"
                        />
                        {renderSuggestionsDropdown('email')}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Company Name 
                      </label>
                      <CustomCreatableDropdown
                        height="38px"
                        minHeight="38px"
                        options={companyDropdown.options}
                        value={companyName ? (companyDropdown.options.find(opt => opt.label === companyName) || { label: companyName, value: companyName }) : null}
                        onChange={(val) => setCompanyName(val ? val.label : "")}
                        onCreateOption={async (val) => {
                          const res = await companyDropdown.onCreateOption(val);
                          if (res && res.success) setCompanyName(val);
                          return res;
                        }}
                        onEditOption={async (id, currentLabel, newText) => {
                          const success = await companyDropdown.onEditOption(id, currentLabel, newText);
                          if (success && companyName === currentLabel) setCompanyName(newText);
                          return success;
                        }}
                        onDeleteOption={async (id, label) => {
                          const success = await companyDropdown.onDeleteOption(id, label);
                          if (success && companyName === label) setCompanyName("");
                          return success;
                        }}
                        isLoading={companyDropdown.isLoading}
                        placeholder="Company Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ABN 
                      </label>
                      <input
                        type="text"
                        value={abn}
                        onChange={(e) => setAbn(formatABN(e.target.value))}
                        className="w-full rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors"
                        placeholder="ABN"
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-100 dark:border-dark-700">
                    <label className="block text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                      Address Details
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Street Address
                        </label>
                        <input
                          type="text"
                          value={streetAddress}
                          onChange={(e) => setStreetAddress(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors"
                          placeholder="123 Example St"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Suburb
                        </label>
                        <CustomCreatableDropdown
                          height="38px"
                          minHeight="38px"
                          options={suburbDropdown.options}
                          value={suburb ? (suburbDropdown.options.find(opt => opt.label === suburb) || { label: suburb, value: suburb }) : null}
                          onChange={(val) => {
                            const label = val ? val.label : "";
                            setSuburb(label);
                            lookupSuburbRecommendation(label);
                          }}
                          onCreateOption={async (val) => {
                            const res = await suburbDropdown.onCreateOption(val);
                            if (res && res.success) setSuburb(val);
                            return res;
                          }}
                          onEditOption={async (id, currentLabel, newText) => {
                            const success = await suburbDropdown.onEditOption(id, currentLabel, newText);
                            if (success && suburb === currentLabel) setSuburb(newText);
                            return success;
                          }}
                          onDeleteOption={async (id, label) => {
                            const success = await suburbDropdown.onDeleteOption(id, label);
                            if (success && suburb === label) setSuburb("");
                            return success;
                          }}
                          isLoading={suburbDropdown.isLoading}
                          placeholder="Suburb"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                          className="w-full h-[38px] px-3 py-2 bg-white dark:bg-dark-900 border border-gray-300 dark:border-dark-600 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors duration-200 text-gray-900 dark:text-gray-100"
                          placeholder="Postal Code"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          State
                        </label>
                        <CustomCreatableDropdown
                          height="38px"
                          minHeight="38px"
                          options={stateDropdown.options}
                          value={state ? (stateDropdown.options.find(opt => opt.label === state) || { label: state, value: state }) : null}
                          onChange={(val) => setState(val ? val.label : "")}
                          onCreateOption={async (val) => {
                            const res = await stateDropdown.onCreateOption(val);
                            if (res && res.success) setState(val);
                            return res;
                          }}
                          onEditOption={async (id, currentLabel, newText) => {
                            const success = await stateDropdown.onEditOption(id, currentLabel, newText);
                            if (success && state === currentLabel) setState(newText);
                            return success;
                          }}
                          onDeleteOption={async (id, label) => {
                            const success = await stateDropdown.onDeleteOption(id, label);
                            if (success && state === label) setState("");
                            return success;
                          }}
                          isLoading={stateDropdown.isLoading}
                          placeholder="State"
                        />
                      </div>
                    </div>
                  </div>

                  </div>

                  <div className="shrink-0 mt-6 flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-dark-700">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-dark-800 dark:text-gray-300 dark:border-dark-600 dark:hover:bg-dark-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                    >
                      {isSubmitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Contact')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
